import { useToast } from '@app/core/notifications';
import {
  socialExportGetRudoFoldersQueryKey,
  useSocialExportCreateRudoFolder,
} from '@mi-front/api-v2';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { rudoMessages } from '../rudo-messages';

type Props = {
  connectionId: string;
};

/**
 * Opens a dialog to create a Rudo folder, then selects it in the parent form.
 */
export function RudoCreateFolderControl({ connectionId }: Props) {
  const { $t } = useIntl();
  const { notifyError, notifySuccess } = useToast();
  const queryClient = useQueryClient();
  const { setValue } = useFormContext();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const createMutation = useSocialExportCreateRudoFolder({
    mutation: {
      onSuccess: async (folder) => {
        await queryClient.invalidateQueries({
          queryKey: socialExportGetRudoFoldersQueryKey(connectionId),
        });
        setValue('folderId', folder.id, { shouldDirty: true, shouldValidate: true });
        notifySuccess($t(rudoMessages.createFolderSuccess));
        setOpen(false);
        setName('');
      },
      onError: (error: unknown) => {
        notifyError((error as Error).message);
      },
    },
  });

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed || !connectionId) return;
    createMutation.mutate({ connectionId, data: { name: trimmed } });
  };

  return (
    <>
      <Button variant="text" size="small" onClick={() => setOpen(true)} disabled={!connectionId}>
        {$t(rudoMessages.createFolder)}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{$t(rudoMessages.createFolderTitle)}</DialogTitle>
        <DialogContent>
          <Stack sx={{ pt: 1 }}>
            <TextField
              label={$t(rudoMessages.createFolderName)}
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
              autoFocus
              required
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={createMutation.isPending}>
            {$t(rudoMessages.createFolderCancel)}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!name.trim() || createMutation.isPending}
          >
            {$t(rudoMessages.createFolderSubmit)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
